import { useEffect, useMemo, useState } from 'react';
import { Badge } from '../ui';
import { checkPieceSeo, type SeoCheck, type SeoStatus } from '../../lib/seoChecks';
import { fetchPieceLibrary, type ContentPieceDetail } from '../../lib/contentPieces';
import type { ContentBrief } from '../../lib/contentJobs';

/**
 * PieceSeo — the SEO readout for a content piece.
 *
 * Measured from the live html every time this renders. Nothing is stored:
 * images land after the build and humans edit in the drawer, so a score frozen
 * at build time would be wrong exactly where it matters most.
 *
 * There is no overall score on purpose. A single number becomes a target, and
 * people write to raise it instead of writing well.
 */

export interface PieceSeoProps {
  piece: ContentPieceDetail;
  /** Live html — may be newer than piece.html while an edit is saving. */
  html: string;
}

const TONE: Record<SeoStatus, 'down' | 'warn' | 'up'> = { fail: 'down', warn: 'warn', pass: 'up' };
const GROUP_LABEL: Record<SeoStatus, string> = {
  fail: 'Needs attention',
  warn: 'Worth a look',
  pass: 'Clear',
};

function Row({ check }: { check: SeoCheck }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: '12px',
        flexWrap: 'wrap',
        padding: '9px 0',
        borderBottom: '1px solid var(--border-hairline)',
      }}
    >
      <span style={{ minWidth: '150px', fontSize: 'var(--text-sm)', color: 'var(--text-body)', fontWeight: 500 }}>
        {check.label}
      </span>
      <span className="t-num" style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', flex: 1, minWidth: '160px' }}>
        {check.detail}
      </span>
      {check.hint ? (
        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', flexBasis: '100%', lineHeight: 1.5 }}>
          {check.hint}
        </span>
      ) : null}
    </div>
  );
}

export default function PieceSeo({ piece, html }: PieceSeoProps) {
  const [library, setLibrary] = useState<{ title: string; html: string }[]>([]);
  const [showPassing, setShowPassing] = useState(false);

  const brief = (piece.job?.brief ?? null) as ContentBrief | null;

  useEffect(() => {
    let live = true;
    if (!piece.client_id) return undefined;
    fetchPieceLibrary(piece.client_id, piece.id)
      .then((rows) => {
        if (live) setLibrary(rows);
      })
      .catch((err) => console.error('SEO library fetch failed:', err));
    return () => {
      live = false;
    };
  }, [piece.client_id, piece.id]);

  const result = useMemo(
    () =>
      checkPieceSeo(html, {
        primary: brief?.primary ?? null,
        secondary: brief?.secondary ?? [],
        metaTitle: piece.title,
        library,
      }),
    [html, brief?.primary, brief?.secondary, piece.title, library],
  );

  const grouped: SeoStatus[] = ['fail', 'warn', 'pass'];

  return (
    <div style={{ maxWidth: '760px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '18px' }}>
        <Badge tone={result.counts.fail ? 'down' : result.counts.warn ? 'warn' : 'up'} dot>
          {result.counts.fail
            ? `${result.counts.fail} needs attention`
            : result.counts.warn
              ? `${result.counts.warn} worth a look`
              : 'All checks clear'}
        </Badge>
        <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
          {result.words.toLocaleString()} words
        </span>
        {!brief?.primary ? (
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
            No primary phrase on this job's brief, so keyword checks are skipped.
          </span>
        ) : null}
      </div>

      {grouped.map((status) => {
        const rows = result.checks.filter((c) => c.status === status);
        if (!rows.length) return null;
        const collapsed = status === 'pass' && !showPassing;
        return (
          <section key={status} style={{ marginBottom: '22px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <Badge tone={TONE[status]} dot>
                {GROUP_LABEL[status]}
              </Badge>
              <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
                {rows.length}
              </span>
              {status === 'pass' ? (
                <button
                  type="button"
                  className="pressable"
                  onClick={() => setShowPassing((v) => !v)}
                  style={{
                    border: 'none',
                    background: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    fontSize: 'var(--text-xs)',
                    color: 'var(--text-muted)',
                    textDecoration: 'underline',
                  }}
                >
                  {collapsed ? 'Show' : 'Hide'}
                </button>
              ) : null}
            </div>
            {collapsed ? null : rows.map((c) => <Row key={c.id} check={c} />)}
          </section>
        );
      })}
    </div>
  );
}
